import { useMemo } from 'react';
import { scaleLinear } from 'd3';
import { CHART_CONFIG, CHART_MARGIN, OverTimeLayoutOption } from '../constants';
import { YAxisExtentDomain } from '../types';

interface UseChartScalesProps {
  yearMin: number;
  yearMax: number;
  chartWidth: number;
  chartHeight: number;
  overTimeLayout: OverTimeLayoutOption;
  yAxisExtentDomain?: YAxisExtentDomain;
}

export const useChartScales = ({
  yearMin,
  yearMax,
  chartWidth,
  chartHeight,
  overTimeLayout,
  yAxisExtentDomain,
}: UseChartScalesProps) => {
  const chartMargin = useMemo(
    () => ({
      left: CHART_MARGIN.LEFT + CHART_MARGIN.Y_AXIS_SELECTOR_WIDTH - 60, // Adjust for base margin
      right: CHART_MARGIN.RIGHT,
      top: CHART_MARGIN.TOP,
      bottom: CHART_MARGIN.BOTTOM,
    }),
    []
  );

  const xScale = useMemo(
    () =>
      scaleLinear()
        .domain([yearMin, yearMax])
        .range([chartMargin.left, chartWidth - chartMargin.right]),
    [yearMin, yearMax, chartMargin.left, chartMargin.right, chartWidth]
  );

  const yScale = useMemo(() => {
    const scale = scaleLinear().range([
      chartHeight - chartMargin.bottom,
      chartMargin.top,
    ]);

    if (overTimeLayout === OverTimeLayoutOption.Share) {
      scale.domain([0, 1]);
    } else {
      if (yAxisExtentDomain) {
        scale
          .domain([
            yAxisExtentDomain.min,
            yAxisExtentDomain.max * CHART_CONFIG.AXIS_EXTENT_MULTIPLIER,
          ])
          .nice();
      }
    }

    return scale;
  }, [
    chartHeight,
    chartMargin.bottom,
    chartMargin.top,
    overTimeLayout,
    yAxisExtentDomain,
  ]);

  return {
    xScale,
    yScale,
    chartMargin,
  };
};

